import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import { AnalyticsQueryParams, AnalyticsTrendQueryParams } from '../analytics-shared';
import { SafetySummary, SafetyTrendPoint, SafetyEventsListResponse, SafetyEventsQueryParams } from './safety-analytics-api.models';

/**
 * Pure API service for safety analytics HTTP operations
 * No state management — just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class SafetyAnalyticsApiService {
  private readonly logger = inject(LoggerService).scope('SafetyAnalyticsApiService');

  private readonly baseUrl = `${environment.API_BASE_URL}/protected/care/analytics/safety`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /protected/care/analytics/safety/summary
   * Aggregated safety metrics for a date range.
   */
  public getSummary(params: AnalyticsQueryParams): Observable<SafetySummary> {
    const httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    return this.http.get<SafetySummary>(`${this.baseUrl}/summary`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching safety summary:', error);
        return throwError(() => new Error('Failed to fetch safety summary'));
      })
    );
  }

  /**
   * GET /protected/care/analytics/safety/trend
   * Time-bucketed trend data for chart visualization.
   * bucket_size defaults to 'week' on the backend if not provided.
   */
  public getTrend(params: AnalyticsTrendQueryParams): Observable<SafetyTrendPoint[]> {
    let httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    if (params.bucket_size) {
      httpParams = httpParams.set('bucket_size', params.bucket_size);
    }

    return this.http.get<SafetyTrendPoint[]>(`${this.baseUrl}/trend`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching safety trend:', error);
        return throwError(() => new Error('Failed to fetch safety trend'));
      })
    );
  }

  /**
   * GET /protected/care/analytics/safety/events
   * Chronological list of individual safety events.
   * limit defaults to 50 on the backend if not provided.
   */
  public getEvents(params: SafetyEventsQueryParams): Observable<SafetyEventsListResponse> {
    let httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<SafetyEventsListResponse>(`${this.baseUrl}/events`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching safety events:', error);
        return throwError(() => new Error('Failed to fetch safety events'));
      })
    );
  }
}
